import { getPatternTemplate } from './catalog';
import type { PatternConfig } from './types';

/**
 * Module-level cache keyed by the full config. The builder is a pure function of its config,
 * so a global cache is safe and survives across both React renders and the (non-React) section
 * SSR render path. Keyspace is tiny in practice (few patterns × few colors).
 */
const layerCache = new Map<string, string>();

const clamp01 = (value: number): number => (Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0);

/** Extract r,g,b from a #RGB / #RGBA / #RRGGBB / #RRGGBBAA hex. Falls back to black. */
const hexToRgb = (hex: string): [number, number, number] => {
  const cleaned = (hex || '').trim().replace(/^#/, '');
  let h = cleaned;
  if (h.length === 3 || h.length === 4) {
    h = h
      .slice(0, 3)
      .split('')
      .map((char) => char + char)
      .join('');
  } else {
    h = h.slice(0, 6);
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return [0, 0, 0];
  return [Number.parseInt(h.slice(0, 2), 16), Number.parseInt(h.slice(2, 4), 16), Number.parseInt(h.slice(4, 6), 16)];
};

/** Build an `rgba(...)` string from a hex color + 0–1 opacity. Opacity is baked here, never via CSS opacity. */
const toRgba = (color: string, opacity: number): string => {
  const [r, g, b] = hexToRgb(color);
  const alpha = Math.round(clamp01(opacity) * 1000) / 1000;
  return `rgba(${r},${g},${b},${alpha})`;
};

/**
 * Build a single CSS `background` layer for a pattern config:
 *   url("data:image/svg+xml,<encoded>") 0 0 / <size>px <size>px repeat
 *
 * The whole SVG is `encodeURIComponent`-encoded so a `#` in any color becomes `%23` (a raw `#`
 * in a data-URI is a fatal fragment delimiter) and quotes/angle brackets are escaped too.
 * Returns '' for an absent or unknown pattern so callers can cleanly fall back to the base.
 */
export const buildPatternBackgroundLayer = (config: PatternConfig | null | undefined): string => {
  if (!config?.patternId) return '';
  const template = getPatternTemplate(config.patternId);
  if (!template) return '';

  const cacheKey = `${config.patternId}|${config.color}|${config.opacity}|${config.scale}`;
  const cached = layerCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const fill = toRgba(config.color, config.opacity);
  const encoded = encodeURIComponent(template.render(fill));
  const size = Math.max(1, Math.round(template.tileSize * (config.scale || 1)));
  const layer = `url("data:image/svg+xml,${encoded}") 0 0 / ${size}px ${size}px repeat`;

  layerCache.set(cacheKey, layer);
  return layer;
};
